import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Avatar, 
  Button,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface Follower {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  profilePhoto?: {
    fileKey: string;
    fileType: string;
    featured: boolean;
    url: string;
  };
  type: string;
}

interface FollowRequest {
  _id: string;
  from: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePhoto?: {
      fileKey: string;
      fileType: string;
      featured: boolean;
      url: string;
    };
    type: string;
  };
  createdAt?: string;
}

const FollowersScreen: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [followRequests, setFollowRequests] = useState<FollowRequest[]>([]);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Only fetch follow requests
        const followRequestsRes = await request(`/profile/follow-requests/incoming`, {
          method: 'GET',
          authorization: true,
        }) as any;

        // Set follow requests
        const requests: FollowRequest[] = followRequestsRes?.data?.requests || followRequestsRes?.data || [];
        setFollowRequests(requests);
      } catch (error) {
        console.error('Error fetching follow requests:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAcceptRequest = async (requestId: string) => {
    setProcessingRequest(requestId);
    try {
      await request(`/profile/follow-requests/${requestId}/accept`, {
        method: 'POST',
        authorization: true,
      });
      
      // Remove the accepted request from the list
      setFollowRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (error) {
      console.error('Error accepting follow request:', error);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    setProcessingRequest(requestId);
    try {
      await request(`/profile/follow-requests/${requestId}/reject`, {
        method: 'POST',
        authorization: true,
      });
      
      // Remove the rejected request from the list
      setFollowRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (error) {
      console.error('Error rejecting follow request:', error);
    } finally {
      setProcessingRequest(null);
    }
  };


  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#ffffff'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      backgroundColor: '#ffffff'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        px: 3, 
        py: 3,
        backgroundColor: '#ffffff'
      }}>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            mr: 3,
            color: '#000',
            p: 0
          }}
        >
          <ArrowBackIcon sx={{ fontSize: '24px' }} />
        </IconButton>
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          color: '#000',
          fontSize: '20px'
        }}>
          Follow Requests
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#f0f0f0', py: 2 }}>
        {/* Follow Requests List */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ px: 2, py: 1, fontWeight: 'bold', color: '#000' }}>
            Follow requests
          </Typography>
          <Box sx={{ px: 2 }}>
            {followRequests.map((request) => (
              <Box 
                key={request._id} 
                sx={{ 
                  backgroundColor: 'white', 
                  mb: 1, 
                  borderRadius: "16px", 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Avatar sx={{ width: 50, height: 50, mr: 2 }} src={request.from.profilePhoto?.url}>
                    {request.from.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#000' }}>
                      {request.from.username}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {request.from.firstName} {request.from.lastName}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#FF6B35',
                      color: 'white',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      minWidth: 80,
                      '&:hover': {
                        backgroundColor: '#E55A2B',
                      }
                    }}
                    disabled={processingRequest === request._id}
                    onClick={() => handleAcceptRequest(request.from._id)}
                  >
                    {processingRequest === request._id ? <CircularProgress size={16} color="inherit" /> : 'Confirm'}
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: '#000',
                      color: '#000',
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      minWidth: 80,
                      '&:hover': {
                        borderColor: '#333',
                        backgroundColor: '#f5f5f5',
                      }
                    }}
                    disabled={processingRequest === request._id}
                    onClick={() => handleRejectRequest(request.from._id)}
                  >
                    {processingRequest === request._id ? <CircularProgress size={16} /> : 'Delete'}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
          {followRequests.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 2, px: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No follow requests
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FollowersScreen;
